package com.arenaproplay.admin.ui.map

import kotlinx.browser.window
import kotlinx.coroutines.CancellationException
import kotlinx.coroutines.await
import org.w3c.fetch.Response

/** Reverse-geocode result — a human address plus the parts the club form stores. */
data class ReverseGeoResult(
    val formattedAddress: String,
    val city: String?,
    val country: String?,
)

/**
 * Resolve coordinates to an address via Nominatim (OpenStreetMap). Used when the
 * user drags the picker map to fine-tune the pin, so the address field follows
 * the new location. `accept-language=en` mirrors the autocomplete calls so city
 * names stay in English. Requires `nominatim.openstreetmap.org` in the CSP
 * `connect-src` (see build.gradle.kts).
 */
suspend fun reverseGeocode(lat: Double, lng: Double): ReverseGeoResult? = try {
    val url = "https://nominatim.openstreetmap.org/reverse" +
        "?format=json&lat=$lat&lon=$lng&zoom=18&addressdetails=1&accept-language=en"
    val response: Response = window.fetch(url).await()
    if (!response.ok) {
        null
    } else {
        val json = JSON.parse<dynamic>(response.text().await())
        val addr: dynamic = json.address
        val city = (addr?.city ?: addr?.town ?: addr?.village ?: addr?.municipality) as? String
        val street = (addr?.road ?: addr?.pedestrian ?: addr?.footway) as? String
        val houseNumber = addr?.house_number as? String
        val country = addr?.country as? String
        val displayName = json.display_name as? String
        val parts = listOfNotNull(street, houseNumber, city, country).filter { it.isNotBlank() }
        val formatted = if (parts.isNotEmpty()) {
            parts.joinToString(", ")
        } else {
            displayName?.substringBefore(",")?.trim().orEmpty()
        }
        if (formatted.isBlank()) null else ReverseGeoResult(formatted, city, country)
    }
} catch (e: CancellationException) {
    throw e
} catch (_: Throwable) {
    null
}
